/* eslint-disable no-nested-ternary, no-plusplus, react/prop-types */
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import {
  Container,
  Grid,
  Typography,
  Stack,
  Button,
  Box,
  Card,
  CardHeader,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  CircularProgress,
  IconButton,
  Tooltip,
  InputAdornment,
  useTheme,
  alpha,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  Plus,
  Copy,
  Layers,
  TrendingUp,
  Search,
  MousePointerClick,
  Download,
  Check,
  Smartphone,
  Globe,
  GitCompare,
  Eye,
  Trash2,
} from 'lucide-react';

// components
import Page from '../components/Page';
import { fCurrency, fNumber, fShortenNumber } from '../utils/formatNumber';
import * as campaignsApi from '../api/campaigns.api';
import { getAppSetting } from '../api/services';

// ----------------------------------------------------------------------

export default function AdCampaigns() {
  const theme = useTheme();
  const navigate = useNavigate();

  // Load from database
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await campaignsApi.getCampaigns();
      if (response.data && response.data.success) {
        setCampaigns(response.data.data);
      }
      const settingsRes = await getAppSetting();
      if (settingsRes.ok && settingsRes.data?.data?.defaultCampaignRedirectUrl) {
        setFormBaseUrl(settingsRes.data.data.defaultCampaignRedirectUrl);
      }
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  // Modal / Copy links states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  // Form states
  const [formName, setFormName] = useState('');
  const [formPlatform, setFormPlatform] = useState('Android');
  const [formChannel, setFormChannel] = useState('Google Ads');
  const [formBudget, setFormBudget] = useState('');
  const [formSpent, setFormSpent] = useState('');
  const [formStartDate, setFormStartDate] = useState('');
  const [formEndDate, setFormEndDate] = useState('');
  const [formBaseUrl, setFormBaseUrl] = useState('');
  const [formUtmSource, setFormUtmSource] = useState('');
  const [formUtmMedium, setFormUtmMedium] = useState('');
  const [formUtmCampaign, setFormUtmCampaign] = useState('');
  const [showAdvancedUtm, setShowAdvancedUtm] = useState(false);

  // Auto-generate UTM Parameters based on Campaign Name and Channel selection
  useEffect(() => {
    let source = 'other';
    let medium = 'referral';
    switch (formChannel) {
      case 'Google Ads':
        source = 'google';
        medium = 'cpc';
        break;
      case 'Facebook Ads':
        source = 'facebook';
        medium = 'cpc';
        break;
      case 'TikTok':
        source = 'tiktok';
        medium = 'influencer';
        break;
      case 'Instagram':
        source = 'instagram';
        medium = 'cpc';
        break;
      case 'Twitter':
      case 'Twitter / X':
        source = 'twitter';
        medium = 'cpc';
        break;
      case 'Influencer':
        source = 'influencer';
        medium = 'social';
        break;
      case 'Email Newsletter':
        source = 'newsletter';
        medium = 'email';
        break;
      default:
        source = 'other';
        medium = 'referral';
    }
    setFormUtmSource(source);
    setFormUtmMedium(medium);

    if (formName) {
      const slug = formName
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/[\s-]+/g, '_');
      setFormUtmCampaign(slug);
    } else {
      setFormUtmCampaign('');
    }
  }, [formName, formChannel]);
  // Aggregate Metrics
  const metrics = useMemo(() => {
    const totalBudget = campaigns.reduce((acc, c) => acc + c.budget, 0);
    const totalSpent = campaigns.reduce((acc, c) => acc + c.spent, 0);
    const totalClicks = campaigns.reduce((acc, c) => acc + c.clicks, 0);
    const totalInstalls = campaigns.reduce((acc, c) => acc + c.installs, 0);
    const totalRevenue = campaigns.reduce((acc, c) => acc + c.revenue, 0);
    const averageConvRate = totalClicks > 0 ? (totalInstalls / totalClicks) * 100 : 0;
    const overallRoi = totalSpent > 0 ? ((totalRevenue - totalSpent) / totalSpent) * 100 : 0;

    return {
      totalBudget,
      totalSpent,
      totalClicks,
      totalInstalls,
      totalRevenue,
      averageConvRate,
      overallRoi,
    };
  }, [campaigns]);

  // Filtered Campaigns list
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((c) => {
      const matchesSearch =
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.channel.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.utmCampaign.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesPlatform = platformFilter === 'All' || c.platform === platformFilter;
      const matchesStatus = statusFilter === 'All' || c.status === statusFilter;

      return matchesSearch && matchesPlatform && matchesStatus;
    });
  }, [campaigns, searchQuery, platformFilter, statusFilter]);

  const buildAttributionUrl = (c) => {
    if (!c) return '';
    const params = new URLSearchParams();
    if (c.utmSource) params.append('utm_source', c.utmSource);
    if (c.utmMedium) params.append('utm_medium', c.utmMedium);
    if (c.utmCampaign) params.append('utm_campaign', c.utmCampaign);
    if (c.utmSource && c.utmMedium && c.utmCampaign) {
      params.append('referrer', `utm_source=${c.utmSource}&utm_medium=${c.utmMedium}&utm_campaign=${c.utmCampaign}`);
    }
    return `${c.baseUrl}?${params.toString()}`;
  };

  const handleCopyLink = (campaign) => {
    const url = buildAttributionUrl(campaign);
    navigator.clipboard.writeText(url);
    setCopiedId(campaign.id);
    toast.success('Attribution link copied!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setFormName('');
    setFormPlatform('Android');
    setFormChannel('Google Ads');
    setFormBudget('');
    setFormSpent('');
    setFormStartDate('');
    setFormEndDate('');
    setFormUtmSource('');
    setFormUtmMedium('');
    setFormUtmCampaign('');
    setShowAdvancedUtm(false);
  };

  const handleSubmitCreate = async (e) => {
    if (e) e.preventDefault();

    if (!formName || !formBudget || !formSpent) {
      toast.warning('Please enter Campaign Name, Budget, and Ad Cost Spent');
      return;
    }

    const budgetVal = Number(formBudget);
    const spentVal = Number(formSpent);

    if (Number.isNaN(budgetVal) || Number.isNaN(spentVal)) {
      toast.error('Budget and Spent values must be numbers');
      return;
    }

    const cleanName = formName.trim();
    const newCampId = formUtmCampaign || cleanName.toLowerCase().replace(/\s+/g, '_');

    const payload = {
      id: newCampId,
      name: cleanName,
      platform: formPlatform,
      channel: formChannel,
      budget: budgetVal,
      spent: spentVal,
      clicks: 0,
      installs: 0,
      revenue: 0,
      status: 'Active',
      utmSource: formUtmSource || 'google',
      utmMedium: formUtmMedium || 'cpc',
      utmCampaign: formUtmCampaign || newCampId,
      baseUrl: formBaseUrl || '',
      startDate: formStartDate || new Date().toISOString().split('T')[0],
      endDate: formEndDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    };

    try {
      const response = await campaignsApi.createCampaign(payload);
      if (response.data && response.data.success) {
        toast.success('Ad campaign created successfully!');
        fetchCampaigns();
        handleCloseCreateDialog();
      } else {
        toast.error(response.data.message || 'Failed to create campaign');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create campaign on server');
    }
  };

  const toggleCampaignStatus = async (id, currentStatus) => {
    const nextStatus = currentStatus === 'Active' ? 'Paused' : currentStatus === 'Paused' ? 'Completed' : 'Active';
    try {
      const response = await campaignsApi.updateCampaign(id, { status: nextStatus });
      if (response.data && response.data.success) {
        toast.success(`Campaign status updated to ${nextStatus}`);
        fetchCampaigns();
      }
    } catch (error) {
      toast.error('Failed to update campaign status');
    }
  };

  const handleDeleteCampaign = async (id) => {
    // eslint-disable-next-line no-alert
    if (
      window.confirm(
        'Are you sure you want to delete this campaign? This will remove all associated user registrations.'
      )
    ) {
      try {
        const response = await campaignsApi.deleteCampaign(id);
        if (response.data && response.data.success) {
          toast.success('Campaign deleted successfully');
          fetchCampaigns();
        }
      } catch (error) {
        toast.error('Failed to delete campaign');
      }
    }
  };

  const handleExportCSV = () => {
    if (filteredCampaigns.length === 0) {
      toast.warning('No campaigns available to export');
      return;
    }
    const headers = [
      'id',
      'name',
      'platform',
      'channel',
      'budget',
      'spent',
      'clicks',
      'installs',
      'revenue',
      'status',
      'utmSource',
      'utmMedium',
      'utmCampaign',
      'startDate',
      'endDate',
    ];
    const csvRows = [headers.join(',')];

    filteredCampaigns.forEach((row) => {
      const values = headers.map((header) => {
        const val = row[header] !== undefined ? row[header] : '';
        const escaped = String(val).replace(/"/g, '\\"');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `ad_campaigns_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Campaigns list exported as CSV');
  };

  return (
    <Page title="Ad Campaigns Control Center">
      <Container maxWidth="xl">
        {/* Title Header with actions */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          justifyContent="space-between"
          mb={4}
          spacing={2}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: '-0.5px' }}
            >
              Ad Campaign Control Center
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
              Track custom referrer links, monitor conversion rates, and measure ROI based on wallet funding
              transactions.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GitCompare size={20} />}
              onClick={() => navigate('/dashboard/campaigns/compare')}
              sx={{
                borderColor: '#DFE3E8',
                color: 'text.primary',
                py: 1.2,
                px: 3,
                borderRadius: '8px',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              Compare Campaigns
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Plus size={20} />}
              onClick={handleOpenCreateDialog}
              sx={{
                backgroundColor: theme.palette.primary.main,
                boxShadow: 'none',
                py: 1.2,
                px: 3,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Create Ad Link
            </Button>
          </Stack>
        </Stack>

        {/* Simple Dashboard Overview Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <AppleMetricWidget
              label="Configured Campaigns"
              value={fNumber(campaigns.length)}
              description={`${campaigns.filter((c) => c.status === 'Active').length} currently active`}
              icon={<Layers size={20} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppleMetricWidget
              label="Total Ad Clicks"
              value={fNumber(metrics.totalClicks)}
              description="Referrals mapped on landing redirects"
              icon={<MousePointerClick size={20} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppleMetricWidget
              label="Attributed Installs"
              value={fNumber(metrics.totalInstalls)}
              description={`Avg. Conversion Rate: ${metrics.averageConvRate.toFixed(1)}%`}
              icon={<Download size={20} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppleMetricWidget
              label="Wallet Revenue Generated"
              value={`₦${fCurrency(metrics.totalRevenue)}`}
              description={`Est. ROI: ${metrics.overallRoi >= 0 ? '+' : ''}${metrics.overallRoi.toFixed(0)}%`}
              icon={<TrendingUp size={20} />}
            />
          </Grid>
        </Grid>

        {/* Main Clean Table Section */}
        <Card sx={{ border: '1px solid #E5E5E7', boxShadow: 'none', borderRadius: '12px' }}>
          <CardHeader
            title="Campaigns Listing"
            subheader="Click on any campaign's details to view dedicated charts, settings, and registration ledgers."
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
            action={
              <Stack direction="row" spacing={1.5} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search campaign name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 200 }}
                />
                <Box sx={{ width: 110 }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={platformFilter}
                    onChange={(e) => setPlatformFilter(e.target.value)}
                  >
                    <MenuItem value="All">All Platforms</MenuItem>
                    <MenuItem value="Android">Android</MenuItem>
                    <MenuItem value="iOS">iOS</MenuItem>
                    <MenuItem value="Web">Web</MenuItem>
                  </TextField>
                </Box>
                <Box sx={{ width: 110 }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="All">All Status</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Paused">Paused</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                  </TextField>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download size={16} />}
                  onClick={handleExportCSV}
                  sx={{
                    height: 40,
                    borderColor: '#DFE3E8',
                    color: 'text.secondary',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  Export CSV
                </Button>
              </Stack>
            }
          />

          <Box sx={{ mt: 2, overflowX: 'auto' }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                <TableRow>
                  <TableCell sx={{ pl: 3 }}>Campaign Details</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Cost Spent / Budget</TableCell>
                  <TableCell align="right">Referrals</TableCell>
                  <TableCell align="right">Installs</TableCell>
                  <TableCell align="right">Wallet Revenue</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center" sx={{ pr: 3 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        Loading campaigns...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCampaigns.map((row) => {
                    const spentPercent = row.budget > 0 ? (row.spent / row.budget) * 100 : 0;
                    const conversionRate = row.clicks > 0 ? (row.installs / row.clicks) * 100 : 0;

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/dashboard/campaigns/${row.id}`)}
                      >
                        <TableCell sx={{ pl: 3 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {row.platform === 'Android' && <Smartphone size={16} color="#A4C639" />}
                            {row.platform === 'iOS' && <Smartphone size={16} color="#555555" />}
                            {row.platform === 'Web' && <Globe size={16} color="#1890FF" />}
                            <Box>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{ color: theme.palette.primary.main }}
                              >
                                {row.name}
                              </Typography>
                              {row.startDate && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {row.startDate} to {row.endDate}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {row.channel}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            utm: {row.utmCampaign}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: 160 }}>
                          <Typography variant="caption" color="text.secondary">
                            ₦{fCurrency(row.spent)} spent of ₦{fCurrency(row.budget)}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, spentPercent)}
                            color={spentPercent > 90 ? 'error' : spentPercent > 50 ? 'warning' : 'success'}
                            sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {fNumber(row.clicks)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {fNumber(row.installs)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {conversionRate.toFixed(1)}% conv
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ color: theme.palette.success.dark, fontWeight: 600 }}>
                          ₦{fShortenNumber(row.revenue)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCampaignStatus(row.id, row.status);
                            }}
                            color={
                              row.status === 'Active' ? 'success' : row.status === 'Paused' ? 'warning' : 'default'
                            }
                            sx={{ fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ pr: 3 }} onClick={(e) => e.stopPropagation()}>
                          <Box display="flex" justifyContent="center" gap={0.5}>
                            <Tooltip title="View Analytics Details">
                              <IconButton size="small" onClick={() => navigate(`/dashboard/campaigns/${row.id}`)}>
                                <Eye size={16} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy UTM Link">
                              <IconButton size="small" onClick={() => handleCopyLink(row)}>
                                {copiedId === row.id ? (
                                  <Check size={16} color={theme.palette.success.main} />
                                ) : (
                                  <Copy size={16} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Campaign">
                              <IconButton size="small" color="error" onClick={() => handleDeleteCampaign(row.id)}>
                                <Trash2 size={16} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
                {filteredCampaigns.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        No campaigns match your filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Card>

        {/* Modal: Create Campaign */}
        <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
          <form onSubmit={handleSubmitCreate}>
            <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>Configure New Ad Link & Campaign</DialogTitle>
            <DialogContent dividers sx={{ py: 2 }}>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label="Campaign Name"
                  placeholder="e.g. Summer Promo 2026"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                  variant="outlined"
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="OS Platform Target"
                      value={formPlatform}
                      onChange={(e) => setFormPlatform(e.target.value)}
                      variant="outlined"
                    >
                      <MenuItem value="Android">Android</MenuItem>
                      <MenuItem value="iOS">iOS</MenuItem>
                      <MenuItem value="Web">Web</MenuItem>
                      <MenuItem value="All">All (Multi-platform)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Acquisition Channel"
                      value={formChannel}
                      onChange={(e) => setFormChannel(e.target.value)}
                      variant="outlined"
                    >
                      <MenuItem value="Google Ads">Google Ads</MenuItem>
                      <MenuItem value="Facebook Ads">Facebook Ads</MenuItem>
                      <MenuItem value="TikTok">TikTok</MenuItem>
                      <MenuItem value="Instagram">Instagram</MenuItem>
                      <MenuItem value="Twitter">Twitter / X</MenuItem>
                      <MenuItem value="Influencer">Influencer</MenuItem>
                      <MenuItem value="Email Newsletter">Email Newsletter</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Budget Allocation"
                      placeholder="Budget in Naira"
                      value={formBudget}
                      onChange={(e) => setFormBudget(e.target.value)}
                      required
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                      }}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Initial Spent (Optional)"
                      placeholder="Spent in Naira"
                      value={formSpent}
                      onChange={(e) => setFormSpent(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                      }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      value={formStartDate}
                      onChange={(e) => setFormStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End Date"
                      value={formEndDate}
                      onChange={(e) => setFormEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>

                {/* <TextField
                  fullWidth
                  label="App Landing URL / Base URL"
                  value={formBaseUrl}
                  onChange={(e) => setFormBaseUrl(e.target.value)}
                  variant="outlined"
                /> */}

                {/* Visual generated preview */}
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #E5E5E7',
                    borderRadius: '8px',
                    mt: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }} color="text.secondary">
                    Auto-Generated Attribution Link
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Automatically computed from Name & Channel selection to avoid manual UTM complexity.
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      p: 1,
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E5E7',
                      borderRadius: '4px',
                      wordBreak: 'break-all',
                      fontFamily: 'monospace',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {formBaseUrl}?utm_source={formUtmSource}&utm_medium={formUtmMedium}&utm_campaign={formUtmCampaign}
                    {formUtmSource && formUtmMedium && formUtmCampaign
                      ? `&referrer=utm_source%3D${formUtmSource}%26utm_medium%3D${formUtmMedium}%26utm_campaign%3D${formUtmCampaign}`
                      : ''}
                  </Typography>
                </Box>

                {/* Advanced toggler */}
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => setShowAdvancedUtm(!showAdvancedUtm)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {showAdvancedUtm ? 'Hide Advanced UTM Settings' : 'Customize UTM Parameters (Advanced)'}
                  </Button>
                </Box>

                {showAdvancedUtm && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }} color="text.secondary">
                      UTM Attribution Parameters
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="UTM Source"
                          placeholder="e.g. google"
                          value={formUtmSource}
                          onChange={(e) => setFormUtmSource(e.target.value)}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="UTM Medium"
                          placeholder="e.g. cpc"
                          value={formUtmMedium}
                          onChange={(e) => setFormUtmMedium(e.target.value)}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="UTM Campaign"
                          placeholder="e.g. summer_sale"
                          value={formUtmCampaign}
                          onChange={(e) => setFormUtmCampaign(e.target.value)}
                          required
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button onClick={handleCloseCreateDialog} color="inherit">
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Create Campaign
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </Page>
  );
}

// ----------------------------------------------------------------------

// Clean Apple Style Metric Card (No background colors, minimalist borders, high font size contrast)
function AppleMetricWidget({ label, value, description, icon }) {
  return (
    <Card
      sx={{ p: 3, border: '1px solid #E5E5E7', boxShadow: 'none', borderRadius: '12px', backgroundColor: '#FFFFFF' }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
          >
            {label}
          </Typography>
          <Box color="text.secondary">{icon}</Box>
        </Stack>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-1px' }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, fontWeight: 500 }}>
            {description}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
}
